/**
 * 备赛规划预览（demo 改造③）：用 shared 的 buildPrepTimeline 以「今天」为确认日，
 * 从赛事里程碑倒推备赛任务并画成甘特式时间轴。
 *
 * 口径红线：算法只在 @crossmatch/shared（与后端落库用的是同一个函数），前端不做任何日期计算；
 * 预览是规则模板倒推，不是大模型生成——标题据实写明。
 */

import { useMemo } from 'react';
import { buildPrepTimeline, type PrepMilestoneKind } from '@crossmatch/shared';

import type { MilestoneDto } from '../api/types.js';
import { Tag } from './Tag.js';

const DAY_MS = 86_400_000;

function dayMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.NaN : Math.floor(ms / DAY_MS) * DAY_MS;
}

function fmt(iso: string): string {
  // YYYY-MM-DD（buildPrepTimeline 的 dueDate 就是这个格式）
  return iso.slice(0, 10);
}

export function PrepPlanPreview({ milestones }: { milestones: MilestoneDto[] }) {
  // 用本地时区的「今天」，避免 toISOString 的 UTC 日期在凌晨差一天
  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const model = useMemo(() => {
    // MilestoneDto.kind 是宽 string，显式收窄到 shared 的词表类型
    const prepMilestones = milestones.map((milestone) => ({
      kind: milestone.kind as PrepMilestoneKind,
      label: milestone.label,
      dueAt: milestone.dueAt,
    }));
    const tasks = buildPrepTimeline(prepMilestones, todayIso);
    const startMs = dayMs(todayIso);
    const candidateDates = [
      ...tasks.map((task) => dayMs(task.dueDate)),
      ...milestones.map((milestone) => dayMs(milestone.dueAt)),
    ].filter((value) => Number.isFinite(value) && value > startMs);
    const endMs = candidateDates.length > 0 ? Math.max(...candidateDates) : startMs + 30 * DAY_MS;
    const span = Math.max(endMs - startMs, DAY_MS);
    const futureMilestones = milestones
      .map((milestone) => ({ ...milestone, ms: dayMs(milestone.dueAt) }))
      .filter((milestone) => Number.isFinite(milestone.ms) && milestone.ms >= startMs)
      .sort((a, b) => a.ms - b.ms);
    return { tasks, startMs, endMs, span, futureMilestones };
  }, [milestones, todayIso]);

  if (milestones.length === 0) {
    return <p className="field-hint">本赛事还没有里程碑：没有锚点就无法倒推备赛节奏，请联系 organizer 维护里程碑。</p>;
  }
  if (model.tasks.length === 0) {
    return <p className="field-hint">现有里程碑都在过去，倒推不出未来的备赛任务；需要更新里程碑日期。</p>;
  }

  const pct = (ms: number): number => Math.min(100, Math.max(0, ((ms - model.startMs) / model.span) * 100));
  const tickEndIso = new Date(model.endMs).toISOString().slice(0, 10);

  return (
    <div className="prep-preview">
      <div className="prep-chart" role="img" aria-label="备赛规划甘特预览">
        <div className="prep-axis" />
        <span className="prep-tick prep-tick-start">{fmt(todayIso)}（今天）</span>
        <span className="prep-tick prep-tick-end">{tickEndIso}</span>
        {model.futureMilestones.map((milestone) => (
          <span
            key={milestone.id}
            className="prep-milestone"
            style={{ left: `${pct(milestone.ms)}%` }}
            title={`里程碑：${milestone.label}（${fmt(milestone.dueAt)}）`}
          >
            ◆
          </span>
        ))}
        {model.tasks.map((task) => (
          <span
            key={`${task.dueDate}-${task.title}`}
            className={task.compressed ? 'prep-task-dot compressed' : 'prep-task-dot'}
            style={{ left: `${pct(dayMs(task.dueDate))}%` }}
            title={`${task.title}（截止 ${task.dueDate}）`}
          />
        ))}
      </div>

      <ol className="prep-list">
        {model.tasks.map((task) => {
          const daysLeft = Math.ceil((dayMs(task.dueDate) - model.startMs) / DAY_MS);
          return (
            <li key={`${task.dueDate}-${task.title}`} className="prep-item">
              <span className="prep-bar" style={{ width: `${Math.max(pct(dayMs(task.dueDate)), 4)}%` }} />
              <span className="prep-item-title">{task.title}</span>
              <span className="muted small">
                {task.dueDate}
                {daysLeft >= 0 ? ` · 还有 ${daysLeft} 天` : ''}
              </span>
              {task.compressed ? (
                <Tag tone="warn" title="倒推日期早于明天，已收紧到确认日之后">
                  已收紧
                </Tag>
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="chart-note">
        预览由 shared 规则模板从里程碑倒推（与后端落库同一函数），不是大模型生成；点「生成备赛规划」后写入项目看板。
      </p>
    </div>
  );
}
